import React, { useState, useEffect } from 'react';
import './TeamQueuePage.css';
import { supabase, isSupabaseConfigured } from '../services/supabase';

interface QueuedTeam {
  id: string;
  name: string;
  city: string;
  abbreviation: string;
  owner_id: string;
  requested_tier?: number;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

interface UnassignedTeam {
  id: string;
  name: string;
  city: string;
  abbreviation: string;
  tier: number;
  league_id: string | null;
}

interface League {
  id: string;
  name: string;
  tier: number;
}

interface TeamQueuePageProps {
  onNavigate?: (page: string) => void;
}

const TeamQueuePage: React.FC<TeamQueuePageProps> = ({ onNavigate }) => {
  const [queuedTeams, setQueuedTeams] = useState<QueuedTeam[]>([]);
  const [unassignedTeams, setUnassignedTeams] = useState<UnassignedTeam[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    
    if (isSupabaseConfigured() && supabase) {
      // Load queued teams
      const { data: queueData } = await supabase
        .from('team_queue')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (queueData) setQueuedTeams(queueData as QueuedTeam[]);
      
      // Load available leagues
      const { data: leagueData } = await supabase
        .from('leagues')
        .select('*')
        .order('tier', { ascending: true });
      
      if (leagueData) setLeagues(leagueData as League[]);
      
      // Load teams that don't have a league_id
      const { data: allTeams } = await supabase
        .from('teams')
        .select('id, name, city, abbreviation, tier, league_id')
        .order('name', { ascending: true });
      
      if (allTeams) {
        // Filter to teams with no league_id
        const unassigned = (allTeams as any[]).filter(t => !t.league_id);
        console.log('Unassigned teams:', unassigned);
        setUnassignedTeams(unassigned);
      }
    }
    
    setLoading(false);
  }

  const getTierName = (tier: number): string => {
    const names: Record<number, string> = { 1: 'Elite', 2: 'Platinum', 3: 'Gold', 4: 'Silver', 5: 'Bronze' };
    return names[tier] || `Tier ${tier}`;
  };

  const getTierColor = (tier: number): string => {
    const colors: Record<number, string> = { 1: '#b9f2ff', 2: '#e5e4e2', 3: '#ffd700', 4: '#c0c0c0', 5: '#cd7f32' };
    return colors[tier] || '#ccc';
  };

  const handleAssignToLeague = async (queueId: string, leagueId: string) => {
    if (!isSupabaseConfigured() || !supabase) return;

    const queueEntry = queuedTeams.find(t => t.id === queueId);
    if (!queueEntry) return;

    const league = leagues.find(l => l.id === leagueId);
    if (!league) return;

    // Count existing teams in this league to decide conference/division
    const { data: existingTeams } = await supabase
      .from('teams')
      .select('conference, division')
      .eq('league_id', leagueId);
    
    const count = existingTeams?.length || 0;
    
    // Distribute: first 5 American-East, next 5 American-West, next 5 National-East, rest National-West
    let conference: string, division: string;
    if (count < 5) { conference = 'American'; division = 'East'; }
    else if (count < 10) { conference = 'American'; division = 'West'; }
    else if (count < 15) { conference = 'National'; division = 'East'; }
    else { conference = 'National'; division = 'West'; }

    // Create new team in teams table from queue data
    const { data: newTeam, error: teamError } = await supabase.from('teams').insert({
      name: queueEntry.name,
      city: queueEntry.city,
      abbreviation: queueEntry.abbreviation,
      owner_id: queueEntry.owner_id,
      tier: league.tier,
      league_id: leagueId,
      division,
      conference,
      is_ai: false,
    }).select('id').single();

    if (teamError || !newTeam) {
      setMessage(`❌ Failed to create team: ${teamError?.message || 'Unknown error'}`);
      return;
    }

    // Create standings entry
    await supabase.from('league_standings').insert({
      team_id: newTeam.id,
      league_id: leagueId,
      wins: 0,
      losses: 0,
      runs_scored: 0,
      runs_allowed: 0,
      division,
      conference,
    });

    // Remove from queue
    await supabase.from('team_queue').delete().eq('id', queueId);

    setMessage(`✅ ${queueEntry.city} ${queueEntry.name} assigned to ${league.name}!`);
    setTimeout(() => setMessage(''), 3000);
    loadData();
  };

  const handleReject = async (queueId: string) => {
    if (!isSupabaseConfigured() || !supabase) return;
    
    const queueEntry = queuedTeams.find(t => t.id === queueId);
    
    // Refund the team purchase cost (10,000 $BALLS)
    if (queueEntry?.owner_id) {
      try {
        await supabase.rpc('increment_balls_balance', {
          target_user_id: queueEntry.owner_id,
          amount: 10000,
        });
        // Also record the transaction
        await supabase.from('balls_transactions').insert({
          user_id: queueEntry.owner_id,
          amount: 10000,
          type: 'refund',
          description: 'Team purchase rejected by Commissioner — refund',
        });
      } catch (e) {
        console.warn('Refund failed:', e);
      }
    }
    
    await supabase.from('team_queue').update({ status: 'rejected' }).eq('id', queueId);
    setMessage('❌ Team rejected. 10,000 $BALLS refunded to owner.');
    setTimeout(() => setMessage(''), 3000);
    loadData();
  };

  // Auto-fill all unassigned teams to leagues
  const handleAutoFillLeagues = async () => {
    if (!isSupabaseConfigured() || !supabase) return;
    if (unassignedTeams.length === 0) {
      setMessage('No unassigned teams found. Check console for debug info.');
      console.log('Leagues:', leagues);
      console.log('All teams:', unassignedTeams);
      return;
    }

    setMessage('🔄 Auto-filling leagues...');
    console.log('Starting auto-fill with leagues:', leagues);
    console.log('Unassigned teams:', unassignedTeams);
    
    // Get leagues sorted by tier
    const sortedLeagues = [...leagues].sort((a, b) => a.tier - b.tier);
    
    if (sortedLeagues.length === 0) {
      setMessage('❌ No leagues found! Create a league first in Commissioner.');
      return;
    }
    
    // Organize teams by league first
    const leagueTeams = new Map<string, { id: string; name: string }[]>();
    
    // First pass: assign league_id to teams and collect per-league
    for (const team of unassignedTeams) {
      let assigned = false;
      
      for (const league of sortedLeagues) {
        const { data: existingTeams } = await supabase
          .from('teams')
          .select('id')
          .eq('league_id', league.id);
        
        const teamCount = existingTeams?.length || 0;
        
        if (teamCount < 20) {
          await supabase.from('teams').update({
            league_id: league.id,
            tier: league.tier,
          }).eq('id', team.id);
          
          if (!leagueTeams.has(league.id)) {
            leagueTeams.set(league.id, []);
          }
          leagueTeams.get(league.id)!.push(team);
          assigned = true;
          break;
        }
      }
      
      if (!assigned) {
        console.warn(`No league found with space for team ${team.name}`);
      }
    }
    
    // Second pass: assign conferences and divisions per league
    let teamsAssigned = 0;
    
    for (const [leagueId, teams] of Array.from(leagueTeams)) {
      const shuffled = [...teams].sort(() => Math.random() - 0.5);
      
      let americanEast = 0, americanWest = 0;
      let nationalEast = 0, nationalWest = 0;
      
      for (const team of shuffled) {
        const index = americanEast + americanWest + nationalEast + nationalWest;
        const conference: 'American' | 'National' = index < 10 ? 'American' : 'National';
        let division: 'East' | 'West';
        
        if (conference === 'American') {
          division = americanEast < 5 ? 'East' : 'West';
          if (division === 'East') americanEast++;
          else americanWest++;
        } else {
          division = nationalEast < 5 ? 'East' : 'West';
          if (division === 'East') nationalEast++;
          else nationalWest++;
        }
        
        await supabase.from('teams').update({
          conference,
          division,
        }).eq('id', team.id);
        
        // Clear old standings and create fresh
        await supabase.from('league_standings').delete().eq('team_id', team.id);
        
        await supabase.from('league_standings').insert({
          league_id: leagueId,
          team_id: team.id,
          wins: 0,
          losses: 0,
          runs_scored: 0,
          runs_allowed: 0,
          division,
          conference,
        });
        
        teamsAssigned++;
      }
    }
    
    setMessage(`✅ Assigned ${teamsAssigned} teams across ${leagueTeams.size} leagues with proper conferences/divisions!`);
    loadData();
    
    setTimeout(() => setMessage(''), 3000);
  };

  const handleAddDemoTeam = async () => {
    if (!isSupabaseConfigured() || !supabase) return;

    const teamId = `queue_team_${Date.now()}`;
    const teamName = `Demo Team ${Math.floor(Math.random() * 100)}`;

    await supabase.from('team_queue').insert({
      id: teamId,
      name: teamName,
      city: 'Test City',
      abbreviation: teamName.slice(0, 3).toUpperCase(),
      owner_id: 'demo_owner',
      status: 'pending',
      created_at: new Date().toISOString()
    });

    loadData();
  };

  const pendingTeams = queuedTeams.filter(t => t.status === 'pending');

  if (loading) {
    return (
      <div className="team-queue-page">
        <div className="loading-container">
          <div className="spinner" />
          <p>Loading team queue...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="team-queue-page">
      {message && <div className="message">{message}</div>}
      
      {/* Header */}
      <div className="page-header">
        <button className="back-btn" onClick={() => onNavigate?.('commissioner')}>← Back</button>
        <div className="header-content">
          <h1 className="page-title">
            <span className="gradient-text">Team Queue</span>
          </h1>
          <p className="page-subtitle">
            Assign teams to leagues during off-season
          </p>
        </div>
        <button className="add-demo-btn" onClick={handleAddDemoTeam}>+ Add Demo Team</button>
      </div>

      {/* Stats */}
      <div className="queue-stats">
        <div className="stat-card">
          <span className="stat-value">{pendingTeams.length}</span>
          <span className="stat-label">Pending Teams</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{leagues.length}</span>
          <span className="stat-label">Active Leagues</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{unassignedTeams.length}</span>
          <span className="stat-label">Unassigned Teams</span>
        </div>
      </div>

      {/* Auto-Fill Button */}
      {unassignedTeams.length > 0 && (
        <div className="auto-fill-section">
          <button className="auto-fill-btn" onClick={handleAutoFillLeagues}>
            🚀 Auto-Fill All Leagues ({unassignedTeams.length} teams)
          </button>
          <p className="hint">Automatically assign unassigned teams to leagues (first available league, max 20 per league)</p>
        </div>
      )}

      {/* Unassigned Teams */}
      {unassignedTeams.length > 0 && (
        <div className="pending-section">
          <h3>📋 Unassigned Teams</h3>
          <div className="queue-list">
            {unassignedTeams.map(team => (
              <div key={team.id} className="queue-item">
                <div className="team-info">
                  <span className="team-name">{team.city} {team.name}</span>
                  <span className="team-abbr">{team.abbreviation}</span>
                </div>
                <div className="team-actions">
                  <select 
                    onChange={async (e) => {
                      if (e.target.value) {
                        await handleAssignToLeague(team.id, e.target.value);
                      }
                    }}
                    defaultValue=""
                  >
                    <option value="">Select League...</option>
                    {leagues.map(league => (
                      <option key={league.id} value={league.id}>
                        {league.name} ({getTierName(league.tier)})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Leagues Overview */}
      <div className="leagues-overview">
        <h3>Available Leagues</h3>
        <div className="league-cards">
          {leagues.map(league => (
            <div key={league.id} className="league-card" style={{ borderLeftColor: getTierColor(league.tier) }}>
              <span className="league-name">{league.name}</span>
              <span className="league-tier" style={{ background: getTierColor(league.tier) }}>
                {getTierName(league.tier)}
              </span>
            </div>
          ))}
          {leagues.length === 0 && <p className="no-data">No leagues available. Create a league first.</p>}
        </div>
      </div>

      {/* Pending Teams */}
      <div className="pending-section">
        <h3>📋 Pending Team Assignments</h3>
        
        {pendingTeams.length === 0 ? (
          <div className="empty-state">
            <p>No teams waiting for league assignment.</p>
            <p className="hint">Teams purchased will appear here for assignment.</p>
          </div>
        ) : (
          <div className="queue-list">
            {pendingTeams.map(team => (
              <div key={team.id} className="queue-item">
                <div className="team-info">
                  <h4>{team.city} {team.name}</h4>
                  <span className="team-abbr">{team.abbreviation}</span>
                  <span className="request-date">Requested: {new Date(team.created_at).toLocaleDateString()}</span>
                </div>
                
                <div className="team-actions">
                  <select 
                    className="league-select"
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAssignToLeague(team.id, e.target.value);
                      }
                    }}
                    defaultValue=""
                  >
                    <option value="">Assign to League...</option>
                    {leagues.map(league => (
                      <option key={league.id} value={league.id}>
                        {league.name} ({getTierName(league.tier)})
                      </option>
                    ))}
                  </select>
                  
                  <button 
                    className="reject-btn"
                    onClick={() => handleReject(team.id)}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamQueuePage;
